import { ChevronDown } from '../../../../modules/design-system';
import { LABEL_COLOR, LABEL_SIZE, VALUE_COLOR, VALUE_SIZE } from './constants';

export interface ViewOddsTriggerProps {
  /** Mod name shown next to the chevron (e.g. "Normal"). */
  modLabel?: string;
  /** When provided, the trigger renders as a button that toggles the odds
   *  breakdown. When omitted it renders as a static label — used for the
   *  recap at the top of the modal, where tapping it again is redundant. */
  onClick?: () => void;
  /** Whether the breakdown is open — rotates the chevron (button only). */
  isOpen?: boolean;
}

/**
 * Right-hand column of the modern pack-stats block — Figma `1336:86230`.
 * "View Odds" label over a "Normal ⌄" value. Replaces the old
 * "Big Win Chance" stat: tapping it toggles the odds breakdown
 * (popover on desktop, modal on mobile).
 */
export function ViewOddsTrigger({ modLabel = 'Normal', onClick, isOpen = false }: ViewOddsTriggerProps) {
  const content = (
    <>
      <p
        className="font-medium tracking-[-0.1px] whitespace-nowrap leading-[20px]"
        style={{ color: LABEL_COLOR, fontSize: LABEL_SIZE }}
      >
        View Odds
      </p>
      <div className="flex items-center gap-[7px]">
        <p
          className="font-bold tracking-[-0.56px] whitespace-nowrap leading-[28px]"
          style={{ color: VALUE_COLOR, fontSize: VALUE_SIZE }}
        >
          {modLabel}
        </p>
        {/* Chevron only on the interactive block — inside the modal the
            trigger is a static recap with nothing left to expand. */}
        {onClick ? (
          <ChevronDown
            size={13}
            strokeWidth={2.5}
            style={{ color: VALUE_COLOR }}
            className={`shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        ) : null}
      </div>
    </>
  );

  if (!onClick) {
    return <div className="flex flex-col items-end gap-[2px]">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-label="View odds breakdown"
      className="flex cursor-pointer flex-col items-end gap-[2px] rounded-[8px] transition-opacity hover:opacity-80 active:scale-95"
    >
      {content}
    </button>
  );
}
